/**
 * NodeInspector - Slide-out panel for editing node endpoint metadata
 *
 * Shows all endpoints with editable controls and config in accordion style.
 * Delegates endpoint rendering to the shared EndpointControlList component.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { LogList, LEVEL_COLORS } from './LogList';
import { LogToolbar } from './LogToolbar';
import { logger, LogLevel, LogEntry } from '@/logging';

import { useAppState } from '../contexts/AppContext';
import { useRuntime } from '../contexts/RuntimeProvider';
import { EndpointControlList, type EndpointControlItem } from './endpoint/EndpointControlList';
import { resolveVizType } from './node/VisualizationRegistry';
import { getNodeType } from '@/graph/GraphDefinition';
import { definitionService } from '@/serverClient/definitionService';
import { confirm } from './ConfirmDialog';

const LEVEL_ORDER: LogLevel[] = [LogLevel.Error, LogLevel.Info, LogLevel.Trace];

export function NodeInspector() {

  const [logEntries, setLogEntries] = useState<LogEntry[]>(logger.getAll());
  const [enabledLevels, setEnabledLevels] = useState<Set<LogLevel>>(
    new Set([LogLevel.Error, LogLevel.Info])
  );

  const { definitionState, instanceState, setNodeLogLevel, getDefinition, setVizNode, getInstanceCount, getApiDifferences, reloadDefinitionFromLibrary, getLibraryDefinition, setLibraryDefinition, addCustomParam, removeCustomParam } = useRuntime();
  const { inspecting, setInspecting } = useAppState();

  // Add param form state
  const [showAddParam, setShowAddParam] = useState(false);
  const [newParamName, setNewParamName] = useState('');
  const [newParamType, setNewParamType] = useState('number');

  // Subscribe to log updates
  useEffect(() => {
    const unsubscribe = logger.subscribe(() => {
      setLogEntries(logger.getAll());
    });
    return unsubscribe;
  }, []);

  // Filter logs for this node
  const nodeLogEntries = useMemo(() =>
    logEntries.filter(e => e.context === inspecting?.nodeId),
    [logEntries, inspecting?.nodeId]
  );

  // Apply level filter for display
  const filteredLogEntries = useMemo(() =>
    nodeLogEntries.filter(e => enabledLevels.has(e.level)),
    [nodeLogEntries, enabledLevels]
  );

  const toggleLevel = useCallback((level: LogLevel) => {
    setEnabledLevels(prev => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const handleNodeLogLevelChange = useCallback((level: LogLevel) => {
    if (!inspecting) return;
    setNodeLogLevel(inspecting.instanceId, inspecting.nodeId, level);
  }, [inspecting, setNodeLogLevel]);

  const handleClearLogs = useCallback(() => {
    if (!inspecting?.nodeId) return;
    logger.clearByContext(inspecting.nodeId);
    setLogEntries(logger.getAll());
  }, [inspecting?.nodeId]);

  const handleCopyLogs = useCallback(() => {
    const text = nodeLogEntries.slice(-20).map(entry => {
      let line = `[${entry.level}] ${entry.message}`;
      if (entry.stack) {
        line += `\n${entry.stack}`;
      }
      return line;
    }).join('\n\n');
    navigator.clipboard.writeText(text);
  }, [nodeLogEntries]);

  // Get definitionId from instanceState
  const definitionId = instanceState[inspecting?.instanceId ?? '']?.definitionId ?? 'root';
  const node = definitionState[definitionId]?.nodes?.[inspecting?.nodeId ?? ''];

  // Check if this node is a graph node (has a definition)
  const nodeDefinition = node ? getDefinition(node.type) : undefined;
  const isGraphNode = !!nodeDefinition;

  // Check if there's a library version to reload from
  const libraryDefinition = node ? getLibraryDefinition(node.type) : undefined;
  const hasLibraryVersion = !!libraryDefinition;

  // Get available viz nodes for graph nodes (nodes that have visualizations)
  const vizNodes = useMemo(() => {
    if (!nodeDefinition) return [];
    return Object.entries(nodeDefinition.nodes)
      .filter(([_nodeId, nodeConfig]) => {
        const nodeType = getNodeType(nodeConfig);
        return resolveVizType(nodeType, getDefinition) !== undefined;
      })
      .map(([nodeId, nodeConfig]) => ({
        nodeId,
        nodeType: getNodeType(nodeConfig)
      }));
  }, [nodeDefinition, getDefinition]);

  // Build EndpointControlItems from node endpoints
  const endpointItems = useMemo((): EndpointControlItem[] => {
    if (!node || !inspecting) return [];
    return Object.values(node.endpoints).map(endpoint => ({
      instanceId: inspecting.instanceId,
      nodeId: inspecting.nodeId,
      endpoint,
    }));
  }, [node, inspecting]);

  const handleDeleteCustomParam = useCallback((item: EndpointControlItem) => {
    removeCustomParam(item.instanceId, item.nodeId, item.endpoint.id);
  }, [removeCustomParam]);

  // Early returns AFTER all hooks
  if (!inspecting?.nodeId) {
    return null;
  }
  if (!node) {
    return null;
  }

  const nodeLogLevel = node.logLevel;

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{node.label}</div>
          <div style={styles.subtitle}>{node.type} ({inspecting.instanceId})</div>
        </div>
        <button onClick={() => setInspecting(null)} style={styles.closeButton}>×</button>
      </div>

      {/* Endpoints list */}
      <div style={styles.content}>
        {/* Graph node actions: Save to Library, Viz selector */}
        {isGraphNode && (
          <>
            <div style={styles.sectionTitle}>Definition</div>
            <div style={styles.definitionActions}>
              <button
                onClick={async () => {
                  if (!nodeDefinition) return;
                  try {
                    await definitionService.save(nodeDefinition);
                    setLibraryDefinition(nodeDefinition);
                    logger.info('NodeInspector', `Saved definition to library: ${nodeDefinition.id}`);
                  } catch (err) {
                    logger.error('NodeInspector', `Failed to save definition: ${err}`);
                  }
                }}
                style={styles.saveButton}
              >
                Save to Library
              </button>
              {hasLibraryVersion && nodeDefinition && (
                <button
                  onClick={async () => {
                    const defId = nodeDefinition.id;
                    const instanceCount = getInstanceCount(defId);
                    const { removed, added } = getApiDifferences(nodeDefinition, libraryDefinition!);

                    let message = '';
                    if (removed.length > 0) {
                      message += `Warning: The following exposed endpoints will be removed, which may disconnect existing connections:\n• ${removed.join('\n• ')}\n\n`;
                    }
                    if (added.length > 0) {
                      message += `New endpoints will be added:\n• ${added.join('\n• ')}\n\n`;
                    }
                    if (instanceCount > 1) {
                      message += `This will affect ${instanceCount} instances of "${defId}".`;
                    } else {
                      message += `This will reload the definition "${defId}" from the library.`;
                    }

                    const confirmed = await confirm({
                      title: 'Reload from Library',
                      message,
                      confirmLabel: 'Reload',
                      cancelLabel: 'Cancel'
                    });

                    if (confirmed) {
                      try {
                        reloadDefinitionFromLibrary(defId);
                        logger.info('NodeInspector', `Reloaded definition from library: ${defId}`);
                      } catch (err) {
                        logger.error('NodeInspector', `Failed to reload definition: ${err}`);
                      }
                    }
                  }}
                  style={styles.reloadButton}
                >
                  Reload from Library
                </button>
              )}
            </div>

            {vizNodes.length > 0 && (
              <>
                <div style={styles.sectionTitle}>Visualization</div>
                <div style={styles.vizSelector}>
                  <select
                    value={nodeDefinition?.vizNode ?? ''}
                    onChange={(e) => setVizNode(node.type, e.target.value || null)}
                    style={styles.vizSelect}
                  >
                    <option value="">None</option>
                    {vizNodes.map(({ nodeId, nodeType }) => (
                      <option key={nodeId} value={nodeId}>
                        {nodeId} ({nodeType})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </>
        )}

        <div style={styles.sectionTitle}>Endpoints</div>
        <EndpointControlList
          items={endpointItems}
          dataFlowFilter={['param', 'trigger', 'event']}
          nodeType={node.type}
          expandable
          initialExpandedId={inspecting.endpointId}
          onDeleteCustomParam={handleDeleteCustomParam}
        />

        {/* Add custom param button/form for nodes that allow it */}
        {node.allowAddParam && (() => {
          const isEcsEntity = node.type === 'grid.GridInit';
          return (
          <div style={styles.addParamContainer}>
            {showAddParam ? (
              <div style={styles.addParamForm}>
                <input
                  type="text"
                  placeholder={isEcsEntity ? 'Layer name' : 'Param name'}
                  value={newParamName}
                  onChange={(e) => setNewParamName(e.target.value)}
                  style={styles.addParamInput}
                  autoFocus
                />
                {!isEcsEntity && (
                  <select
                    value={newParamType}
                    onChange={(e) => setNewParamType(e.target.value)}
                    style={styles.addParamSelect}
                  >
                    <option value="number">number</option>
                    <option value="string">string</option>
                    <option value="boolean">boolean</option>
                    <option value="array">array</option>
                  </select>
                )}
                <div style={styles.addParamButtons}>
                  <button
                    onClick={() => {
                      if (newParamName.trim()) {
                        if (isEcsEntity) {
                          addCustomParam(inspecting.instanceId, inspecting.nodeId, newParamName.trim(), {
                            value: 0,
                            dataType: 'number',
                            dataFlow: 'internal',
                          });
                        } else {
                          const defaultValues: Record<string, any> = {
                            number: 0,
                            string: '',
                            boolean: false,
                            array: []
                          };
                          addCustomParam(inspecting.instanceId, inspecting.nodeId, newParamName.trim(), {
                            value: defaultValues[newParamType],
                            dataType: newParamType
                          });
                        }
                        setNewParamName('');
                        setShowAddParam(false);
                      }
                    }}
                    style={styles.addParamConfirmButton}
                    disabled={!newParamName.trim()}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddParam(false);
                      setNewParamName('');
                    }}
                    style={styles.addParamCancelButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddParam(true)}
                style={styles.addParamButton}
              >
                {isEcsEntity ? '+ Add Layer' : '+ Add Param'}
              </button>
            )}
          </div>
          );
        })()}

        {/* Node logs */}
        <div style={styles.sectionTitle}>Logs ({filteredLogEntries.length}/{nodeLogEntries.length})</div>
        <div style={styles.logsContainer}>
          {/* Node log level control */}
          <div style={styles.logLevelRow}>
            <span style={styles.logLevelLabel}>Log Level:</span>
            <div style={styles.logLevelButtons}>
              {LEVEL_ORDER.map(level => (
                <button
                  key={level}
                  onClick={() => handleNodeLogLevelChange(level)}
                  style={{
                    ...styles.logLevelButton,
                    background: nodeLogLevel === level ? LEVEL_COLORS[level] : '#333',
                    color: nodeLogLevel === level ? '#fff' : '#666'
                  }}
                  title={`Set node to log ${level} and above`}
                >
                  {level.charAt(0).toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <LogList
            entries={filteredLogEntries}
            maxItems={20}
            compact
            emptyMessage="No logs for this node"
          />
          <LogToolbar
            enabledLevels={enabledLevels}
            onToggleLevel={toggleLevel}
            onClear={handleClearLogs}
            onCopy={handleCopyLogs}
            filteredCount={filteredLogEntries.length}
            totalCount={nodeLogEntries.length}
          />
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 350,
    background: '#1f2937',
    borderLeft: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideIn 0.2s ease-out',
    zIndex: 1000
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 20px',
    borderBottom: '1px solid #374151',
    background: '#374151'
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white'
  },
  subtitle: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: 24,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 0'
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#6b7280',
    padding: '0 20px 8px',
    letterSpacing: '0.05em',
    marginTop: 16
  },
  definitionActions: {
    padding: '0 20px 16px',
    display: 'flex',
    gap: 8
  },
  saveButton: {
    background: '#3b82f6',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500
  },
  reloadButton: {
    background: '#6b7280',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500
  },
  vizSelector: {
    padding: '0 20px 16px'
  },
  vizSelect: {
    width: '100%',
    background: '#374151',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '8px 12px',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer'
  },
  logsContainer: {
    padding: '0 20px'
  },
  logLevelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: '1px solid #374151'
  },
  logLevelLabel: {
    fontSize: 11,
    color: '#9ca3af'
  },
  logLevelButtons: {
    display: 'flex',
    gap: 2
  },
  logLevelButton: {
    border: 'none',
    borderRadius: 3,
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  addParamContainer: {
    padding: '12px 20px',
    borderTop: '1px solid #374151'
  },
  addParamButton: {
    background: '#374151',
    border: '1px dashed #4b5563',
    borderRadius: 4,
    padding: '8px 16px',
    color: '#9ca3af',
    fontSize: 13,
    cursor: 'pointer',
    width: '100%'
  },
  addParamForm: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8
  },
  addParamInput: {
    background: '#374151',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '8px 12px',
    color: 'white',
    fontSize: 13
  },
  addParamSelect: {
    background: '#374151',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '8px 12px',
    color: 'white',
    fontSize: 13
  },
  addParamButtons: {
    display: 'flex',
    gap: 8
  },
  addParamConfirmButton: {
    flex: 1,
    background: '#3b82f6',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer'
  },
  addParamCancelButton: {
    flex: 1,
    background: '#4b5563',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    color: 'white',
    fontSize: 13,
    cursor: 'pointer'
  }
};
